/**
 * Linux Desktop Redux Slice
 * Manages state for Linux desktop sessions
 */

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  listLinuxDesktops,
  createLinuxDesktop,
  getLinuxDesktop,
  deleteLinuxDesktop,
  linuxDesktopAction,
  getLinuxCredentials
} from "../api/linuxApi";

// Async thunks
export const fetchLinuxDesktops = createAsyncThunk(
  "linux/fetchDesktops",
  async (_, { rejectWithValue }) => {
    try {
      const response = await listLinuxDesktops();
      return response.items || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const launchLinuxDesktop = createAsyncThunk(
  "linux/launchDesktop",
  async (params, { rejectWithValue }) => {
    try {
      const response = await createLinuxDesktop(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchLinuxDesktop = createAsyncThunk(
  "linux/fetchDesktop",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getLinuxDesktop(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const removeLinuxDesktop = createAsyncThunk(
  "linux/removeDesktop",
  async (id, { rejectWithValue }) => {
    try {
      await deleteLinuxDesktop(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const performLinuxAction = createAsyncThunk(
  "linux/performAction",
  async ({ id, action }, { rejectWithValue }) => {
    try {
      const response = await linuxDesktopAction(id, action);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchLinuxCredentials = createAsyncThunk(
  "linux/fetchCredentials",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getLinuxCredentials(id);
      return { id, credentials: response };
    } catch (error) {
    const status = error.response?.status;
    const message = error.response?.data?.error || error.response?.data?.message || error.message;
    if (status === 409) {
    return rejectWithValue({ transient: true, status, message });
    }
    return rejectWithValue({ transient: false, status, message });
    }
  }
);

const initialState = {
  desktops: [],
  activeDesktop: null,
  credentials: {},
  loading: false,
  error: null,
  actionLoading: {},
  launchStatus: "idle",
  connectionStatus: "disconnected"
};

const linuxSlice = createSlice({
  name: "linux",
  initialState,
  reducers: {
    setActiveLinuxDesktop: (state, action) => {
      state.activeDesktop = action.payload;
    },
    clearActiveLinuxDesktop: (state) => {
      state.activeDesktop = null;
    },
    setConnectionStatus: (state, action) => {
      state.connectionStatus = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    updateDesktopInList: (state, action) => {
      const index = state.desktops.findIndex((d) => d.id === action.payload.id);
      if (index !== -1) {
        state.desktops[index] = action.payload;
      }
      if (state.activeDesktop?.id === action.payload.id) {
        state.activeDesktop = action.payload;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all desktops
      .addCase(fetchLinuxDesktops.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLinuxDesktops.fulfilled, (state, action) => {
        state.loading = false;
        state.desktops = action.payload;
      })
      .addCase(fetchLinuxDesktops.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Launch desktop
      .addCase(launchLinuxDesktop.pending, (state) => {
        state.launchStatus = "loading";
        state.error = null;
      })
      .addCase(launchLinuxDesktop.fulfilled, (state, action) => {
        state.launchStatus = "succeeded";
        state.desktops.push(action.payload);
        state.activeDesktop = action.payload;
      })
      .addCase(launchLinuxDesktop.rejected, (state, action) => {
        state.launchStatus = "failed";
        state.error = action.payload;
      })

      // Fetch single desktop
      .addCase(fetchLinuxDesktop.fulfilled, (state, action) => {
        const index = state.desktops.findIndex((d) => d.id === action.payload.id);
        if (index !== -1) {
          state.desktops[index] = action.payload;
        } else {
          state.desktops.push(action.payload);
        }
        if (state.activeDesktop?.id === action.payload.id) {
          state.activeDesktop = action.payload;
        }
      })

      // Remove desktop
      .addCase(removeLinuxDesktop.fulfilled, (state, action) => {
        state.desktops = state.desktops.filter((d) => d.id !== action.payload);
        if (state.activeDesktop?.id === action.payload) {
          state.activeDesktop = null;
        }
      })

      // Perform action
      .addCase(performLinuxAction.pending, (state, action) => {
        const id = action.meta.arg.id;
        state.actionLoading[id] = true;
      })
      .addCase(performLinuxAction.fulfilled, (state, action) => {
        const id = action.payload.id;
        delete state.actionLoading[id];
        const index = state.desktops.findIndex((d) => d.id === id);
        if (index !== -1) {
          state.desktops[index] = action.payload;
        }
        if (state.activeDesktop?.id === id) {
          state.activeDesktop = action.payload;
        }
      })
      .addCase(performLinuxAction.rejected, (state, action) => {
        const id = action.meta.arg.id;
        delete state.actionLoading[id];
      })

      // Fetch credentials
      .addCase(fetchLinuxCredentials.fulfilled, (state, action) => {
        state.credentials[action.payload.id] = action.payload.credentials;
      });
  }
});

export const {
  setActiveLinuxDesktop,
  clearActiveLinuxDesktop,
  setConnectionStatus,
  clearError,
  updateDesktopInList
} = linuxSlice.actions;

export default linuxSlice.reducer;
